import { Fragment, useCallback, type FC } from 'react';

import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type ThemeName } from '#packages/web/theme';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { Theme } from '#packages/web/theme';

import { SelectThemeDialog } from '#apps/client-web/features/settings/components/select-theme-dialog';
import { SelectThemeAppearanceDialog } from '#apps/client-web/features/settings/components/select-theme-appearance-dialog';
import { type ThemeAppearance } from '#apps/client-web/features/settings/models/app-settings';

import { type AppSettingsPageViewModel } from './app-settings-page.view-model';
import { UserSettingsPageRenderer } from './app-settings-page.renderer';

export type UserSettingsPageContainerProps =
  ViewModelProps<AppSettingsPageViewModel> & LoggingContext;

export const UserSettingsPageContainer: FC<UserSettingsPageContainerProps> = ({
  viewModel,

  logger,
}) => {
  const { form } = viewModel;

  const { setValue } = form;

  // SelectTheme dialog
  const selectThemeDialog = useDialog<ThemeName>();
  const setThemeName = useCallback(
    (newThemeName: ThemeName) => {
      logger?.debug({ newThemeName }, 'Changing theme name');

      setValue?.('theme.name', newThemeName, { shouldDirty: true });
      selectThemeDialog.close();
    },
    [setValue, selectThemeDialog.close],
  );

  // SelectAppearance dialog
  const selectAppearanceDialog = useDialog<ThemeAppearance>();
  const setThemeAppearance = useCallback(
    (newAppearance: ThemeAppearance) => {
      logger?.debug({ newAppearance }, 'Changing theme appearance');

      setValue?.('theme.appearance', newAppearance, { shouldDirty: true });
      selectAppearanceDialog.close();
    },
    [setValue, selectAppearanceDialog.close],
  );

  const themeOptions = Theme.list[form.getValues().theme.name];

  return (
    <Fragment>
      <UserSettingsPageRenderer
        form={form}
        onSelectThemeDialog={selectThemeDialog.open}
        onSelectThemeAppearanceDialog={selectAppearanceDialog.open}
        themeOptions={themeOptions}
      />

      <SelectThemeDialog
        open={selectThemeDialog.isOpen}
        onFormCancel={selectThemeDialog.close}
        onFormSubmit={setThemeName}
        initialValue={selectThemeDialog.data}
      />

      <SelectThemeAppearanceDialog
        open={selectAppearanceDialog.isOpen}
        onFormCancel={selectAppearanceDialog.close}
        onFormSubmit={setThemeAppearance}
        initialValue={selectAppearanceDialog.data}
      />
    </Fragment>
  );
};
